const INSTRUCTIONS_MARKDOWN = `# Instructions

## Introduction

The Clinical Decision-Making (CDM) sections of this examination consist of a series of 26 cases, each typically including 2 to 4 questions (approximately 70 to 80 questions total). Questions are either in an Extended Multiple Choice or Short Answer format. These cases and questions are presented in 2 sections consisting of 13 cases each.

These instructions and the tutorial will be available for review throughout the CDM section (by clicking the **Instructions** button).

## Cases and Parts

Most CDM cases are divided into multiple parts. For example, in Part 1 of a case, you may be given a clinical scenario and asked what lab test(s) to order. In Part 2, you will be provided with lab test results and asked how to manage the patient's condition. The number of parts is indicated at the beginning of each case.

You must complete the cases in the order in which they are presented. Scenario text and questions from previous parts will be displayed as you progress through a case, but you cannot view or change your answer(s) from previous parts. Also, you will not be able to move forward to the next part until you have submitted your answer(s) to the question(s) in the current part.

## Visuals

When an exhibit is included in a case, it will be representative of relevant normal or abnormal findings needed to respond to the question.

## Question Type 1 of 2: Extended Multiple Choice

Extended Multiple Choice (1 or more correct) questions require you to select your answer(s) from a list of options. For each question, in order to receive credit, you must not exceed the given response limit (for example, 6 are selected when the response limit is 5). Most lists of options have "none," "no tests," or "no action" as the last option.

### Scoring

- Points will be awarded for the question if you select the correct response(s).
- 0 points will be awarded for the question if:
  - The correct response(s) is/are not selected from the list of options.
  - More than the allowed number of options are selected, even if the correct option(s) is/are selected.
  - An option that could cause harm to the patient or involves the unnecessary use of costly resources is selected, even if the correct option(s) is/are selected.
  - A final option like, "No Management Needed," is selected along with another option.

## Question Type 2 of 2: Short Answer

Short Answer questions require you to type your answer(s) in the box(es) provided (100-character limit per box). The number of boxes provided corresponds with the response limit for that question. You should only enter 1 response per box. Each Short Answer response should be concise and limited to a single specific diagnosis, examination step, diagnostic study, or treatment.

### Scoring

- Points will be awarded for the question if you select the correct response(s).
- 0 points will be awarded for the question if:
  - The correct response(s) is/are not selected from the list of options.
  - If you list more than 1 response in a box (for example, "type 2 diabetes, hypothyroidism").
  - A response is entered that could cause harm to the patient or involves the unnecessary use of costly resources, even if the correct response(s) is/are entered in another answer box.

### Short Answer Responses

Please provide specific answers for Short Answer responses.

- **Pharmacologic management:** Specify drug names rather than drug classes. Routes of administration should always be specified. Listing dosages is not necessary.
- **Osteopathic manipulative treatment:** Technique names and anatomic sites must be specified; simply writing "osteopathic manipulative treatment" is insufficient.
- **Imaging studies:** Must include anatomic site.
- **Abbreviations:** Avoid or limit use due to lack of standardization.
- **Protocols:** These will not be accepted as correct because they are often not universal.
- **Stat:** Include "stat" when a procedure or diagnostic test is needed within 30 minutes, regardless of clinical setting, including the emergency department.
- **Referrals and consultations:** If the correct plan of action requires a specialty referral and/or consultation, you must specify the type of specialist and the appropriate procedure or management step the specialist will perform (for example, "refer to psychiatry for cognitive-behavioral therapy").

### Approved Laboratory Test Panels

In Short Answer questions, you can order any specific laboratory test. If you would like to order a panel, you must choose from the provided Approved Laboratory Test Panels list. If the panel you have in mind is not included on this list, the specific test(s) for that panel must be listed individually in separate boxes. The list can be accessed at any time by clicking the **Lab Panels** button.

**Acceptable approved laboratory test panels:**

- Arterial blood gases
- Basic metabolic panel
- Complete blood count
- Electrolyte panel
- Hepatic panel
- Lipid panel
- Serum immunoglobulins
- Urinalysis

**All specific laboratory tests**, for example:

- Albumin
- Potassium
- Thyroid-stimulating hormone
- Total cholesterol
- Total iron-binding capacity
- (Any specific test is acceptable when listed separately)

## Functionality

The CDM sections do not have all of the same functionality as the multiple choice sections of the examination. The features that will not be available during the CDM sections are:

- Marking questions for later review
- Reviewing questions

Although the highlight and strike-out features are available in the CDM sections, highlighted text in the case information will not be carried over as you navigate forward through a CDM case.

## Navigation

- **Next and Previous buttons:** Questions within the same part are separated by Next and Previous buttons. You must enter an answer to move between questions in a part.
- **End of Part Confirmation:** After clicking Next at the end of each part and each case, you will be prompted to confirm that you wish to continue to the next part. If you click the No button, you may change, add, or remove answers. If you click Yes, you will NOT be able to change your previous answers for the case.
- **Scrolling:** For all questions, use the scroll bar on the right of the screen to ensure you have viewed all content, including the entire option list or all provided response boxes.
- **Instructions button:** You may review all of the CDM instructions by clicking the Instructions button in the upper-left corner of the screen.
- **Lab Panel button:** You may review the Approved Laboratory Test Panels and their components by clicking the Lab Panels button in the upper-left corner of the screen.

## CDM Question Types

### Extended Multiple Choice

To select or deselect an answer option, click in the box to the left of the text. Note that the functionality will allow you to select as many answers as are available.

When the "none" option is selected, additional options can be selected. Conversely, if any other option is selected, you will be able to select the "none" option at the same time. This will result in no point being awarded.

### Short-answer questions

To type in your answer(s), move your cursor anywhere within the response box's borders, click your mouse, and then begin typing. To move to the next response box, you can press the Tab key or use your mouse to click inside the response box.
`;

export default INSTRUCTIONS_MARKDOWN;